import React, { useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Box,
  Stack,
  Tooltip,
  Checkbox,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Styles from "./masterTable.module.scss";
import { visuallyHidden } from "@mui/utils";
import CircularProgress from "@mui/material/CircularProgress";

const MasterTable = ({
  columns,
  data,
  handleEdit,
  handleView,
  handleDelete,
  handleMultiSelect,
  tableActionButton,
  openLoader,
  tableTotalCount = 0,
  isServerSide = false,
  pagination = false,
  isInactiveAction = false,
  customRowStyle,
  tableOutputEvent = () => { },
}) => {
  const [order, setOrder] = React.useState("");
  const [orderBy, setOrderBy] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [sortedData, setSortedData] = React.useState([]);
  const [selectedIds, setSelectedIds] = React.useState([]);
  useEffect(() => {
    if (!isServerSide) {
      const sorted = stableSort(data, getComparator(order, orderBy));
      setSortedData(sorted);
    } else {
      setSortedData(data);
      if (data?.length === 0 && tableTotalCount) {
        setPage((prev) => (prev > 0 ? prev - 1 : 0));
        if (page >= 0) {
          tableOutputEvent({
            page: page || 1,
            limit: rowsPerPage,
            sortType: order,
            sort: order === "desc" ? `-${orderBy}` : orderBy,
          });
        }
      }
    }
  }, [data, order, orderBy, isServerSide]);

  const handleRequestSort = (event, property) => {
    setPage(0);
    const isAsc = orderBy === property && order === "asc";
    const orderType = isAsc ? "desc" : "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    if (isServerSide) {
      setSortedData(data);
      tableOutputEvent({
        page: 1,
        limit: rowsPerPage,
        sortType: orderType,
        sort: orderType === "desc" ? `-${property}` : property,
      });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (isServerSide) {
      tableOutputEvent({
        page: newPage + 1,
        limit: rowsPerPage,
        sortType: order,
        sort: order === "desc" ? `-${orderBy}` : orderBy,
      });
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    if (isServerSide) {
      tableOutputEvent({
        page: 1,
        limit: parseInt(event.target.value, 10),
        sortType: order,
        sort: order === "desc" ? `-${orderBy}` : orderBy,
      });
    }
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  const handleSelectAll = () => {
    if (selectedIds.length === sortedData.length) {
      setSelectedIds([]);
    } else {
      const allIds = sortedData.map((row) => row._id);
      setSelectedIds(allIds);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedIds((prevSelectedIds) => {
      if (prevSelectedIds.includes(id)) {
        return prevSelectedIds.filter((selectedId) => selectedId !== id);
      } else {
        return [...prevSelectedIds, id];
      }
    });
  };

  useEffect(() => {
    if (handleMultiSelect) {
      handleMultiSelect(selectedIds);
    } else {
      console.warn("handleMultiSelect function is not provided.");
    }
  }, [selectedIds]);
  // }, [selectedIds, handleMultiSelect]);

  const getComparator = (order, orderBy) => {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  return (
    <TableContainer className={Styles.tableContainer}>
      <Box sx={{ height: "100%" }} className={Styles.tableContent}>
        <Table stickyHeader size="medium">
          <TableHead>
            <TableRow>
              {handleMultiSelect && (
                <TableCell padding="checkbox" className={Styles.TableCell}>
                  <Checkbox
                    color="primary"
                    // indeterminate={
                    //   selectedIds.length > 0 &&
                    //   selectedIds.length < sortedData.length
                    // }
                    checked={
                      sortedData?.length > 0 &&
                      selectedIds.length === sortedData.length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}
              {columns.map((column, index) => (
                <TableCell
                  key={index}
                  sortDirection={orderBy === column.id ? order : false}
                  className={Styles.TableCell}
                  align="left"
                >
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={(e) => handleRequestSort(e, column.id)}
                  >
                    {column.label}
                    {orderBy === column.id ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === "desc"
                          ? "sorted descending"
                          : "sorted ascending"}
                      </Box>
                    ) : (
                      <></>
                    )}
                  </TableSortLabel>
                </TableCell>
              ))}

              {handleEdit || handleDelete || handleView || tableActionButton ? (
                <TableCell className={Styles.TableCell} align="center">
                  Action
                </TableCell>
              ) : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {openLoader ? (
              <TableRow>
                <TableCell
                  colSpan={
                    columns.length +
                    (handleEdit || handleView || handleDelete ? 1 : 0)
                  }
                  sx={{ textAlign: "center" }}
                >
                  <CircularProgress color="success" />
                </TableCell>
              </TableRow>
            ) : sortedData?.length ? (
              (isServerSide
                ? sortedData
                : sortedData.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage
                )
              ).map((row, index) => {
                return (
                  <TableRow hover key={index} sx={{ backgroundColor: "white", ...(customRowStyle ? customRowStyle(row)?.sx : {}) }}>
                    {handleMultiSelect && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={selectedIds.includes(row._id)}
                          onChange={() => handleSelectRow(row._id)}
                        />
                      </TableCell>
                    )}

                    {columns.map((column, index) => (
                      <>
                        <TableCell
                          key={index}
                          className={
                            column?.Styles
                              ? Styles[column?.Styles]
                              : Styles.TableBodyCell
                          }
                          sx={{
                            width: column?.width || 300,
                            maxWidth: column?.width || 300,
                          }}
                        // width={700}
                        >
                          {column?.split ? (
                            <Stack width={"300px"}>
                              {row[column.id]
                                ?.split(column.split)
                                .map((ele, index) => (
                                  <Typography
                                    key={index}
                                    className={Styles.TableBodyCell}
                                    sx={{
                                      color:
                                        row?.flag === true
                                          ? "red"
                                          : row?.flag === false
                                            ? "green"
                                            : "#4d4d4d",
                                    }}
                                  >
                                    {ele}
                                  </Typography>
                                ))}
                            </Stack>
                          ) : (
                            <Typography
                              key={index}
                              className={Styles.TableBodyCell}
                              sx={{
                                color:
                                  row?.subHistoryStatus == "1"
                                    ? "green"
                                    : row?.subHistoryStatus == "2"
                                      ? "red"
                                      : row?.flag === true
                                        ? "red"
                                        : row?.flag === false
                                          ? "green"
                                          : "#4d4d4d",
                              }}
                            >
                              {row[column.id]}
                            </Typography>
                          )}
                        </TableCell>
                      </>
                    ))}
                    <TableCell
                      className={Styles.TableBodyCell}
                      sx={{
                        display:
                          handleEdit ||
                            handleDelete ||
                            handleView ||
                            tableActionButton
                            ? "-ms-grid"
                            : "none",
                      }}
                    >
                      <Box className={Styles.ActionContainer}>
                        <Box
                          sx={{
                            display: handleView ? "block" : "none",
                          }}
                        >
                          <Tooltip title="View">
                            <RemoveRedEyeIcon onClick={() => handleView(row)} />
                          </Tooltip>
                        </Box>
                        {tableActionButton &&
                          tableActionButton.map((button, btnIndex) => {
                            if (button.shouldRender && !button.shouldRender(row)) return null;
                            return (
                              <Tooltip title={button.title} key={btnIndex}>
                                <Box
                                  onClick={() => button.onClick(row)}
                                  className={Styles.actionButton}
                                >
                                  {button.btnIcon}
                                </Box>
                              </Tooltip>
                            );
                          })}
                        <Box
                          sx={{
                            display: handleEdit ? "block" : "none",
                          }}
                        >
                          <Tooltip title="Edit">
                            <EditIcon onClick={() => handleEdit(row)} />
                          </Tooltip>
                        </Box>
                        <Box
                          sx={{
                            display: handleDelete ? "block" : "none",
                          }}
                        >
                          {isInactiveAction ? (
                            row?.status == "3" ? (
                              <Tooltip title="Make Active">
                                <BlockIcon onClick={() => handleDelete(row)} sx={{ color: 'red' }} />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Make Inactive">
                                <CheckCircleIcon onClick={() => handleDelete(row)} sx={{ color: 'green' }} />
                              </Tooltip>
                            )
                          ) : (
                            <Tooltip title="Delete">
                              <DeleteIcon onClick={() => handleDelete(row)} />
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={
                    columns.length +
                    (handleEdit || handleView || handleDelete ? 1 : 0)
                  }
                  sx={{ textAlign: "center" }}
                >
                  Oops No data found ... !
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      {!pagination &&
        <Box className={Styles.PaginationContainer}>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={isServerSide ? tableTotalCount : data?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      }
    </TableContainer>
  );
};

export default MasterTable;
